import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, Plus, Trash2, Loader2, CalendarOff, Pencil, Users, Download } from "lucide-react";

const WD = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const WD_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

const blankWeek = () =>
  WD.map((_, i) => ({ weekday: i, enabled: i < 5, start: "09:00", end: "18:00", break_minutes: 60 }));

function ScheduleForm({ value, onChange }) {
  const setDay = (i, patch) => {
    const weekly = value.weekly.map((d, idx) => (idx === i ? { ...d, ...patch } : d));
    onChange({ ...value, weekly });
  };
  return (
    <div className="space-y-4">
      <div>
        <Label>Nombre del horario *</Label>
        <Input data-testid="sched-name" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder="Ej. Jornada partida L-V" className="mt-1" />
      </div>
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
        <div>
          <div className="text-sm font-medium text-slate-800">Horario flexible</div>
          <div className="text-xs text-slate-400">Permite entrada/salida flexible cumpliendo las horas teóricas</div>
        </div>
        <Switch data-testid="sched-flexible" checked={value.flexible} onCheckedChange={(v) => onChange({ ...value, flexible: v })} />
      </div>
      <div className="space-y-2">
        {value.weekly.map((d, i) => (
          <div key={i} className={`grid grid-cols-[90px_1fr_1fr_80px] items-center gap-2 rounded-lg border px-3 py-2 ${d.enabled ? "border-slate-200" : "border-slate-100 bg-slate-50/60"}`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch data-testid={`sched-day-${i}`} checked={d.enabled} onCheckedChange={(v) => setDay(i, { enabled: v })} />
              <span className="text-sm font-medium text-slate-700">{WD_SHORT[i]}</span>
            </label>
            <Input type="time" value={d.start} disabled={!d.enabled} onChange={(e) => setDay(i, { start: e.target.value })} className="h-9" />
            <Input type="time" value={d.end} disabled={!d.enabled} onChange={(e) => setDay(i, { end: e.target.value })} className="h-9" />
            <Input type="number" min="0" value={d.break_minutes} disabled={!d.enabled} onChange={(e) => setDay(i, { break_minutes: Number(e.target.value) })} className="h-9" title="Pausa (min)" />
          </div>
        ))}
        <div className="grid grid-cols-[90px_1fr_1fr_80px] gap-2 px-3 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          <span>Día</span><span>Entrada</span><span>Salida</span><span>Pausa</span>
        </div>
      </div>
    </div>
  );
}

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", weekly: blankWeek(), flexible: false });
  const [saving, setSaving] = useState(false);
  const [hol, setHol] = useState({ date: "", name: "" });
  const [regions, setRegions] = useState([]);
  const [importRegion, setImportRegion] = useState("nacional");
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([api.get("/schedules"), api.get("/holidays")]);
    setSchedules(s.data); setHolidays(h.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/holidays/regions").then(({ data }) => setRegions(data)).catch(() => {}); }, []);

  const importHolidays = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/holidays/import", { region: importRegion, year: Number(importYear) });
      toast.success(`${data.created} festivos importados (${data.year})`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const openNew = () => { setEditing(null); setForm({ name: "", weekly: blankWeek(), flexible: false }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, weekly: s.weekly?.length ? s.weekly : blankWeek(), flexible: !!s.flexible });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Indica un nombre");
    setSaving(true);
    try {
      if (editing) await api.patch(`/schedules/${editing.id}`, form);
      else await api.post("/schedules", form);
      toast.success(editing ? "Horario actualizado" : "Horario creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeSched = async (id) => {
    if (!window.confirm("¿Eliminar este horario? Se desasignará de los empleados.")) return;
    await api.delete(`/schedules/${id}`); toast.success("Horario eliminado"); load();
  };

  const addHoliday = async () => {
    if (!hol.date || !hol.name.trim()) return toast.error("Indica fecha y nombre");
    try {
      await api.post("/holidays", hol);
      setHol({ date: "", name: "" }); toast.success("Festivo añadido"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeHoliday = async (id) => { await api.delete(`/holidays/${id}`); load(); };

  const dayLabel = (s) => {
    const on = (s.weekly || []).filter((d) => d.enabled);
    if (!on.length) return "Sin días";
    return on.map((d) => WD_SHORT[d.weekday]).join(" ");
  };

  return (
    <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Horarios y Festivos</h1>
            <p className="text-slate-500 text-sm mt-1">Define turnos, jornadas flexibles y días festivos de la empresa.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="brand-btn" data-testid="add-schedule-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nuevo horario</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar horario" : "Nuevo horario"}</DialogTitle></DialogHeader>
              <ScheduleForm value={form} onChange={setForm} />
              <DialogFooter>
                <Button onClick={save} disabled={saving} className="brand-btn" data-testid="save-schedule-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Guardar cambios" : "Crear horario")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="h-48 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <h3 className="font-heading font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> Horarios / turnos</h3>
              {schedules.map((s) => (
                <Card key={s.id} className="p-5 border-slate-200 shadow-none flex items-center justify-between" data-testid={`schedule-${s.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800 truncate">{s.name}</span>
                      {s.flexible && <Badge variant="secondary" className="text-xs">Flexible</Badge>}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">Días: <span className="font-medium">{dayLabel(s)}</span></div>
                    <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Users className="h-3 w-3" /> {s.assigned_count} empleado(s) asignados</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-sched-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="hover:text-red-500" onClick={() => removeSched(s.id)} data-testid={`del-sched-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </Card>
              ))}
              {!schedules.length && <Card className="p-10 text-center text-slate-400 border-slate-200 shadow-none">Aún no has creado horarios.</Card>}
            </div>

            <div className="space-y-3">
              <h3 className="font-heading font-semibold flex items-center gap-2"><CalendarOff className="h-4 w-4 text-slate-400" /> Festivos y días libres</h3>
              <Card className="p-4 border-slate-200 shadow-none space-y-3">
                <div className="rounded-lg bg-slate-50 p-3 space-y-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Importar festivos oficiales</div>
                  <Select value={importRegion} onValueChange={setImportRegion}>
                    <SelectTrigger data-testid="import-region" className="bg-white h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{regions.map((r) => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Input type="number" value={importYear} onChange={(e) => setImportYear(e.target.value)} className="bg-white h-9 w-24" data-testid="import-year" />
                    <Button size="sm" className="brand-btn flex-1 h-9" onClick={importHolidays} disabled={importing} data-testid="import-holidays-btn">
                      {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Importar"}
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400">Festivos nacionales + de tu Comunidad. Los locales/provinciales se añaden a mano abajo.</p>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Input type="date" value={hol.date} onChange={(e) => setHol({ ...hol, date: e.target.value })} data-testid="holiday-date" />
                  <Input value={hol.name} onChange={(e) => setHol({ ...hol, name: e.target.value })} placeholder="Nombre del festivo" data-testid="holiday-name" />
                  <Button size="sm" variant="outline" onClick={addHoliday} data-testid="add-holiday-btn"><Plus className="h-4 w-4 mr-1" /> Añadir festivo</Button>
                </div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {holidays.map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`holiday-${h.id}`}>
                      <div>
                        <span className="font-medium text-slate-700">{new Date(h.date + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}</span>
                        <span className="text-slate-400 ml-2">{h.name}</span>
                      </div>
                      <button onClick={() => removeHoliday(h.id)} className="text-slate-300 hover:text-red-500" data-testid={`del-holiday-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {!holidays.length && <div className="text-center text-slate-400 text-sm py-4">Sin festivos definidos.</div>}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
  );
}
